<template>
  <BCard>
    <div class="flex items-center mb-[1.75rem]">
      <div>
        <h1 class="text-black text-2xl font-semibold mb-[.5rem]">
          Kelola Partner
        </h1>
        <p class="m-0 text-[#737373]">
          Manage talent assignment dan review status SK dari partner.
        </p>
      </div>
      <BButton
        variant="primary"
        class="ms-auto"
        @click="$router.push('/assignment/manage-partners/create')"
      >
        <div class="inline-block flex items-center">
          <span class="k-add scale-110" />
          <span class="ms-[.75rem]">Assign Partner Baru</span>
        </div>
      </BButton>
    </div>

    <div>
      <!-- Filter -->
      <div class="flex items-center gap-[1rem] mb-[1.75rem]">
        <div class="d-flex rounded-lg align-items-center border search-bar me-auto">
          <span
            class="k-search-normal-1 h-100 font-bold text-10 align-middle mx-[5px]"
          />
          <BFormInput
            v-model="searchKey"
            placeholder="Cari Nama atau No. Partner, Talent atau ID Talent"
            class="border-0"
            @input="onSearchPartner"
          />
        </div>
        <div class="manage-partner-tool-container ps-[.75rem] group">
          <span class="k-add-circle" />
          <VSelect
            v-model="selectedTalentSkill"
            :reduce="e => e.id"
            :options="talentSkills"
            :clearable="false"
            :disabled="false"
            placeholder="Layanan"
            class="plain-vue-select solid-placeholder-vue-select w-fit group-focus-within:min-w-[250px] min-w-[100px]"
            @input="validateParams()"
          >
            <template #open-indicator>
              <span />
            </template>
            <template #selected-option="{ name }">
              <span class="text-black font-medium whitespace-nowrap">{{ name }}</span>
            </template>
            <template #option="{ name }">
              <span class="whitespace-nowrap">{{ name }}</span>
            </template>
          </VSelect>
        </div>
        <div class="px-[.75rem] py-[.5rem] manage-partner-tool-container">
          <date-range-picker
            ref="date-range-picker"
            v-model="dateRange"
            :locale-data="locale"
            :ranges="ranges"
            :max-date="new Date()"
            opens="left"
            direction="down"
            :auto-apply="true"
            class="w-fit"
            control-container-class="border-0"
            @update="validateParams"
          >
            <template v-slot:input="{ click: openDatePicker }">
              <BButton
                variant="link"
                class="manage-partner-tool-btn-left"
                @click="() => openDatePicker()"
              >
                <div class="inline-block flex items-center">
                  <span class="k-add-circle scale-110" />
                  <span class="ms-[.75rem]">Tanggal Assignment</span>
                </div>
              </BButton>
              <span
                v-if="dateRange.startDate && dateRange.endDate"
                class="text-black"
              >
                {{ `${DAY_MONTH_YEAR(dateRange.startDate)} - ${DAY_MONTH_YEAR(dateRange.endDate)}` }}
              </span>
              <span
                v-else
                class="text-[#737373]"
              >Pilih Tanggal
              </span>
            </template>
          </date-range-picker>
        </div>
        <BButton
          variant="outline-primary"
          :disabled="!(searchKey || dateRange.startDate || dateRange.endDate || selectedTalentSkill)"
          @click="() => {
            resetFilter()
            validateParams()
          }"
        >
          <div class="inline-block flex items-center">
            <feather-icon
              icon="XIcon"
              size="16"
            />
            <span class="ms-[.75rem]">Reset</span>
          </div>
        </BButton>
      </div>
      <!-- Partners -->
      <BOverlay
        id="scroll"
        :show="isLoadingData"
        spinner-variant="primary"
        variant="light"
        blur="0"
        opacity=".5"
        rounded="sm"
      >
        <div
          ref="partner-card-list"
          class="grid grid-cols-3 gap-[1rem] overflow-y-auto h-[60vh]"
        >
          <div
            v-if="partnerList.length < 1 && !isLoadingData && searchKey"
            class="col-span-3 flex flex-col gap-[.75rem] items-center text-center"
          >
            <div class="inline-block aspect-ratio--1x1 rounded-lg bg-[#F5F5F5] p-[.5rem] mx-auto">
              <feather-icon
                icon="ListIcon"
                size="24"
              />
            </div>
            <p class="m-0 font-medium text-xl text-black">
              Tidak ada data yang cocok dengan pencarian
            </p>
            <p class="m-0 w-[300px]">
              Data yang kamu cari tidak ditemukan.
            </p>
          </div>
          <div
            v-else-if="partnerList.length < 1 && !isLoadingData"
            class="col-span-3 flex flex-col gap-[.75rem] items-center text-center"
          >
            <div class="inline-block aspect-ratio--1x1 rounded-lg bg-[#F5F5F5] p-[.5rem] mx-auto">
              <feather-icon
                icon="ListIcon"
                size="24"
              />
            </div>
            <p class="m-0 font-medium text-xl text-black">
              Belum Ada Partner Aktif
            </p>
            <p class="m-0 w-[300px]">
              Data partner akan muncul disini jika kamu sudah menambahkan data.
            </p>
          </div>
          <PartnerCard
            v-for="partner in partnerList"
            :key="partner.partner_id"
            :data="partner"
          />
          <div
            ref="partnerScrollSentinel"
          />
        </div>
      </BOverlay>
    </div>
  </BCard>
</template>

<style scoped>
.btn-primary,
.btn-outline-primary,
.manage-partner-tool-container {
  border-radius: 10px;
}

.manage-partner-tool-container {
  display: flex;
  align-items: center;
}

.manage-partner-tool-container {
  color: black !important;
  border: 2px dashed #E5E5E5 !important;
}

.btn-outline-primary {
  padding: .75rem 1rem !important;
}

.manage-partner-tool-btn-left.btn-link {
  color: black !important;
  border-right: 1px solid #E5E5E5;
  padding: .25rem .5rem .25rem .25rem !important;
  margin-right: .5rem !important;
}
</style>

<script>
import {
  firstDateOfMonth, last7, today, yesterday,
} from '@/store/helpers'
import DateRangePicker from 'vue2-daterange-picker'
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'
import { DATE_NUMBER_DASH_REVERSE, DAY_MONTH_YEAR } from '@/libs/filterDate'
import VSelect from 'vue-select'
import { komtimAxiosIns } from '@/libs/axios'
import PartnerCard from './PartnerCard.vue'
import '@core/scss/vue/libs/vue-select.scss'

export default {
  components: { DateRangePicker, VSelect, PartnerCard },
  data() {
    return {
      searchKey: null,
      dateRange: {
        startDate: null,
        endDate: null,
      },
      locale: {
        format: 'dd/mm/yyyy',
        applyLabel: 'Terapkan',
        cancelLabel: 'Batal',
        customRangeLabel: 'Custom Range',
      },
      ranges: {
        'Hari ini': [today, today],
        Kemarin: [yesterday, yesterday],
        '7 Hari Terakhir': [last7, today],
        'Bulan ini': [firstDateOfMonth, today],
      },
      partnerList: [],
      limit: 10,
      offset: 0,
      isLastData: false,
      isLoadingData: false,
      selectedTalentSkill: null,
      talentSkills: [],
      partnerObserver: null,
      isFirstLoad: true,
    }
  },
  mounted() {
    this.validateParams()
    this.getListSkills()
    this.initPartnerObserver()
  },
  beforeDestroy() {
    if (this.partnerObserver) {
      this.partnerObserver.disconnect()
    }
  },
  methods: {
    DAY_MONTH_YEAR,
    /**
     * Reset filter
     */
    resetFilter() {
      this.searchKey = null
      this.dateRange.startDate = null
      this.dateRange.endDate = null
      this.selectedTalentSkill = null
    },
    /**
     * Validate stored search params.
     * Restore previous search keyword or load default data.
     */
    validateParams() {
      if (this.searchKey !== '' || this.selectedTalentSkill || this.dateRange.startDate || this.dateRange.endDate) {
        this.onSearchPartner()
        return
      }
      this.partnerList = []
      this.offset = 0
      this.getPartnerList()
    },
    /**
     * Debounced search handler.
     * Reload data based on current keyword.
     */
    onSearchPartner: _.debounce(async function search() {
      this.offset = 0
      this.isLastData = false
      this.partnerList = []
      await this.getPartnerList()
    }, 1000),
    /**
     * Get partner list
    */
    async getPartnerList() {
      try {
        this.isLoadingData = true
        const params = {
          offset: this.offset,
          limit: this.limit,
          keyword: this.searchKey,
          skill_id: this.selectedTalentSkill,
          start_date: this.dateRange.startDate ? DATE_NUMBER_DASH_REVERSE(this.dateRange.startDate) : null,
          end_date: this.dateRange.endDate ? DATE_NUMBER_DASH_REVERSE(this.dateRange.endDate) : null,
        }
        const url = '/v1/partner_assignments'

        const { data: { data } } = await komtimAxiosIns.get(url, { params })
        if (data.length < this.limit) {
          this.isLastData = true
        } else {
          this.isLastData = false
        }
        this.partnerList.push(...data)
        this.offset += data.length
      } catch {
        this.partnerList = []
      } finally {
        this.isLoadingData = false
      }
    },
    initPartnerObserver() {
      const sentinel = this.$refs.partnerScrollSentinel
      const container = this.$refs['partner-card-list']
      if (!sentinel || !container) return
      this.partnerObserver = new IntersectionObserver(
        entries => {
          const entry = entries[0]
          if (entry.isIntersecting && !this.isLastData && !this.isLoadingData) {
            if (this.isFirstLoad) {
              this.isFirstLoad = false
              return
            }
            this.getPartnerList()
          }
        },
        { root: container, threshold: 0.1 },
      )
      this.partnerObserver.observe(sentinel)
    },
    async getListSkills() {
      const params = `active=${true}&type=2`
      const url = `v1/skills/resource?${params}`

      await komtimAxiosIns
        .get(url)
        .then(res => {
          const { data } = res.data
          this.talentSkills = data
        })
    },
  },
}
</script>
